import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { Seo } from '@/components/common/Seo';
import { FadeIn } from '@/components/common/FadeIn';
import { PageHero } from '@/components/common/PageHero';
import { OphirButton } from '@/components/common/OphirButton';
import { useContent } from '@/context/ContentContext';
import { CATEGORY_DETAILS, genericDetail } from '@/data/categoryDetails';

const DISCLAIMER =
  'All product opportunities are subject to commercial review, market suitability, supplier approval, documentation, regulatory requirements, inventory availability and agreed purchasing terms.';

export default function CategoryDetail() {
  const { slug } = useParams();
  const { content, loading } = useContent();
  const categories = content?.categories || [];
  const category = categories.find((c) => c.slug === slug);

  // Wait for content before deciding the route is invalid.
  if (loading && !category) return null;
  if (!category) return <Navigate to="/categories" replace />;

  const detail = CATEGORY_DETAILS[slug] || genericDetail(category.title);
  const origin = typeof window !== 'undefined' ? window.location.origin : '';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.title} | Ophir Supply`,
    description: detail.seoDescription,
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${origin}/` },
        { '@type': 'ListItem', position: 2, name: 'Product Categories', item: `${origin}/categories` },
        { '@type': 'ListItem', position: 3, name: category.title, item: `${origin}/categories/${slug}` },
      ],
    },
  };

  return (
    <>
      <Seo title={detail.seoTitle} description={detail.seoDescription} jsonLd={jsonLd} jsonLdId="ophir-category-jsonld" />

      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" data-testid="category-breadcrumb" className="bg-ophir-ivory-soft">
        <div className="container-ophir pt-6">
          <ol className="flex flex-wrap items-center gap-1.5 text-[13px] text-ophir-muted">
            <li><Link to="/" className="hover:text-ophir-navy transition-colors">Home</Link></li>
            <li aria-hidden="true"><ChevronRight size={14} className="text-ophir-grey-med" /></li>
            <li><Link to="/categories" className="hover:text-ophir-navy transition-colors">Product Categories</Link></li>
            <li aria-hidden="true"><ChevronRight size={14} className="text-ophir-grey-med" /></li>
            <li className="text-ophir-navy font-medium" aria-current="page">{category.title}</li>
          </ol>
        </div>
      </nav>

      <PageHero
        label="Product category"
        title={category.title}
        intro={detail.heroIntro || category.blurb}
        image={category.image}
        imageAlt={detail.imageAlt}
      />

      <section className="bg-white">
        <div className="container-ophir py-16 lg:py-24">
          <div className="max-w-3xl">
            <h2 className="font-serif text-[26px] sm:text-[30px] leading-tight text-ophir-navy-deep">{detail.heading}</h2>
            <div className="mt-5 space-y-4">
              {detail.intro.map((p, i) => (
                <p key={i} className="text-[16.5px] leading-relaxed text-ophir-muted">{p}</p>
              ))}
            </div>
          </div>

          {detail.groups.length > 0 && (
            <div className="mt-14">
              <h3 className="font-serif text-[20px] text-ophir-navy-deep">{detail.groupsTitle || 'Assortment areas'}</h3>
              <p className="mt-2 text-[14.5px] text-ophir-muted max-w-2xl">
                {detail.groupsSubtitle || 'Our assortment may include products across the following areas. Availability varies by supplier, brand, product category and destination.'}
              </p>
              <div className="mt-7 grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                {detail.groups.map((g, i) => (
                  <FadeIn key={g.title} delay={(i % 3) * 0.05}>
                    <div data-testid="category-group" className="h-full bg-ophir-ivory-soft border border-ophir-grey rounded-2xl p-6 shadow-ophir">
                      <h4 className="font-serif text-[17px] text-ophir-navy-deep">{g.title}</h4>
                      <ul className="mt-4 flex flex-wrap gap-2">
                        {g.items.map((item) => (
                          <li key={item} className="rounded-full border border-ophir-grey bg-white px-3 py-1.5 text-[13px] text-ophir-navy">
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </FadeIn>
                ))}
              </div>
            </div>
          )}

          {/* Built for Professional Purchasing */}
          {detail.buyerTypes && (
            <div className="mt-14" data-testid="category-buyer-types">
              <h3 className="font-serif text-[20px] text-ophir-navy-deep">{detail.buyerTypes.heading}</h3>
              {detail.buyerTypes.body && (
                <p className="mt-2 text-[15.5px] leading-relaxed text-ophir-muted max-w-3xl">{detail.buyerTypes.body}</p>
              )}
              <ul className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3">
                {detail.buyerTypes.items.map((it) => (
                  <li key={it} className="flex items-start gap-2.5 text-[15px] text-ophir-navy">
                    <span className="mt-2 h-1.5 w-1.5 rounded-full bg-ophir-gold shrink-0" />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Supplier section */}
          {detail.supplierSection && (
            <div className="mt-14 rounded-2xl border border-ophir-navy/15 bg-ophir-ivory-soft p-8" data-testid="category-supplier-section">
              <div className="gold-rule w-12 mb-5" />
              <h3 className="font-serif text-[22px] text-ophir-navy-deep">{detail.supplierSection.heading}</h3>
              <p className="mt-3 text-[15.5px] leading-relaxed text-ophir-muted max-w-3xl">{detail.supplierSection.body}</p>
              <div className="mt-6">
                <OphirButton to={detail.supplierSection.ctaTo || '/suppliers'} data-testid="category-supplier-cta">{detail.supplierSection.ctaLabel || 'Submit Your Product Line'}</OphirButton>
              </div>
            </div>
          )}

          {/* Buyer CTA */}
          <div className="mt-14 rounded-2xl border border-ophir-grey bg-ophir-ivory-soft p-8">
            <h3 className="font-serif text-[22px] text-ophir-navy-deep">{detail.cta?.heading || `Interested in ${category.title.toLowerCase()} supply?`}</h3>
            <p className="mt-2 text-[15.5px] leading-relaxed text-ophir-muted max-w-2xl">
              {detail.cta?.body || 'Wholesale opportunities may be available. Contact us regarding product availability and how we can support your purchasing requirements.'}
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <OphirButton to={detail.cta?.primaryTo || '/request-bulk-quote'} data-testid="category-cta-availability">{detail.cta?.primaryLabel || 'Request Product Availability'}</OphirButton>
              <OphirButton variant="secondary" to={detail.cta?.secondaryTo || '/contact'} data-testid="category-cta-contact">{detail.cta?.secondaryLabel || 'Contact Our Sales Team'}</OphirButton>
            </div>
          </div>

          <div className="mt-10 text-[13.5px] leading-relaxed text-ophir-muted">{DISCLAIMER}</div>

          <div className="mt-8">
            <Link to="/categories" className="inline-flex items-center gap-1.5 text-[14px] text-ophir-navy hover:text-ophir-navy-deep transition-colors">
              <ChevronRight size={16} className="rotate-180" /> Back to all categories
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
